// crewbit-admin/src/hooks/useResolvedClusterConfig.ts
//
// Industry Engine Phase 1b — async reader for the cluster-config substrate.
//
// Loads the two reference tables — cluster_config (7 rows) and
// sub_industry_override (17 rows), migration 20260522131252 — once per mount,
// then runs the pure resolveClusterConfig() resolver over them. Mirrors the
// useIndustryCopyTokens reference-data pattern: per-component async fetch, no
// module-level cache.
//
// tenant_override is created EMPTY in Phase 1 (Phase 3 writes it), so the hook
// passes tenantOverride: null — the cascade degrades cleanly to the cluster
// default. When a tenant_override writer ships, extend the hook to also fetch
// the business's row.
//
// Body byte-equivalent to crewbit-mobile/hooks/useResolvedClusterConfig.ts
// modulo import paths (paired-module rule).
import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';
import {
  resolveClusterConfig,
  type ClusterConfigRow,
  type ResolvedClusterConfig,
  type SubIndustryOverrideRow,
} from '../data/resolveClusterConfig';

const CLUSTER_CONFIG_COLUMNS =
  'cluster_key, default_landing_zoom, default_landing_lens, fab_shape, ' +
  'recurring_bias, resource_primitive, has_property_entity, has_vehicle_entity, ' +
  'show_route_lens, work_dropdown_surfaces';
const SUB_INDUSTRY_OVERRIDE_COLUMNS =
  'category_key, cluster_key, behavior_overrides, routing_overrides';

export interface UseResolvedClusterConfigResult {
  config: ResolvedClusterConfig;
  loading: boolean;
}

/**
 * Resolve a business's cluster config from the DB-backed substrate.
 *
 * `config` is always a complete ResolvedClusterConfig — the resolver's
 * crash-guard default while the reference rows load, then the resolved value.
 * `loading` is true until the fetch settles.
 */
export function useResolvedClusterConfig(
  categoryId: string | null | undefined,
): UseResolvedClusterConfigResult {
  const [clusterConfigs, setClusterConfigs] = useState<ClusterConfigRow[]>([]);
  const [subIndustryOverrides, setSubIndustryOverrides] = useState<SubIndustryOverrideRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [clusterRes, subRes] = await Promise.all([
        supabase.from('cluster_config').select(CLUSTER_CONFIG_COLUMNS),
        supabase.from('sub_industry_override').select(SUB_INDUSTRY_OVERRIDE_COLUMNS),
      ]);
      if (cancelled || !mountedRef.current) return;
      if (clusterRes.error || subRes.error) {
        log.warn('[cluster-config] reference load failed', {
          clusterError: clusterRes.error?.message,
          subError: subRes.error?.message,
        });
      }
      setClusterConfigs((clusterRes.data as ClusterConfigRow[] | null) ?? []);
      setSubIndustryOverrides((subRes.data as SubIndustryOverrideRow[] | null) ?? []);
      setLoading(false);
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  const config = useMemo(
    () =>
      resolveClusterConfig({
        categoryId,
        clusterConfigs,
        subIndustryOverrides,
        tenantOverride: null,
      }),
    [categoryId, clusterConfigs, subIndustryOverrides],
  );

  return { config, loading };
}
